import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import Link from 'next/link';

import { toFooterSettingsShape } from 'lib/util/footerSettingsShape';
import { ReduxState } from 'ducks';
import { SnsIcons } from 'components/Sns/SnsIcons';

import styles from './BookingWidgetFooter.module.css';
import baseStyles from '../base.module.css';
import { useEssentialPageLinks } from 'hooks/useEssentialPageLinks';
import { useCustomFooterLinks } from 'hooks/useCustomFooterLinks';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { appendQueryString } from 'lib/util/appendQueryString';
import { useQueryString } from 'hooks/useQueryString';

const BookingWidgetFooter = () => {
  const { i18n, t } = useTranslation();

  const { useApiKeyInPaths, apiKey } = React.useContext(ApiKeyContext);

  const queryString = useQueryString();

  const settings = useSelector((state: ReduxState) =>
    toFooterSettingsShape(state.server.settings.all)
  );

  const customFooterLinks = useCustomFooterLinks();

  const essentialPageLinks = useEssentialPageLinks();

  return (
    <footer
      className={clsx(
        styles['footer'],
        baseStyles['color-txt--white'],
        baseStyles['color-bg--black']
      )}
    >
      {essentialPageLinks.length > 0 && (
        <div className={styles['footer__nav']}>
          <div className={styles['footer__nav__box']}>
            <ul className={styles['footer__nav__box__list']}>
              {essentialPageLinks.map((link, idx) => (
                <li className={styles['footer__nav__box__list__item']} key={idx}>
                  <Link href={link.url}>
                    <a>{link.text}</a>
                  </Link>
                </li>
              ))}
              {customFooterLinks.map((footerLink, idx) => (
                <li className={styles['footer__nav__box__list__item']} key={idx}>
                  {footerLink.custom_page_path ? (
                    <Link
                      href={appendQueryString(
                        useApiKeyInPaths
                          ? `/${apiKey}${footerLink.custom_page_path}`
                          : footerLink.custom_page_path,
                        queryString
                      )}
                    >
                      <a>{footerLink.text}</a>
                    </Link>
                  ) : (
                    <a href={footerLink.url} target="_blank" rel="noopener noreferrer">
                      {footerLink.text}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <div className={clsx(styles['footer__inner'])}>
        <div className={styles['footer__info']}>
          <ul>
            <li>
              <img src="/static/images/coolProfessional/ic_phone.svg" />
              {settings.phone}
            </li>
            <li>
              <img src="/static/images/coolProfessional/ic_mail.svg" />
              {settings.email}
            </li>
          </ul>
          <ul>
            <li>
              <img src="/static/images/coolProfessional/ic_location.svg" />
              {settings.address}
            </li>
            <li>
              <img src="/static/images/coolProfessional/ic_time.svg" />
              {t('Hours: {{operatingHoursDescription}}', {
                operatingHoursDescription: settings.operatingHoursDescription,
              })}
            </li>
          </ul>
          <SnsIcons snsIds={settings?.snsIds} />
        </div>
        <div className={styles['footer__menu']}>
          {i18n.language === 'ja-JP' && settings.showJapaneseTerms && (
            <ul>
              <li>
                <a href="https://app.ntmg.com/ja/terms" target="_blank" rel="noopener noreferrer">
                  特定商取引法
                </a>
              </li>
            </ul>
          )}
        </div>
      </div>
      <p className={styles['footer__copy']}>© 2022 NutmegLabs Inc.</p>
    </footer>
  );
};

export default BookingWidgetFooter;
